import { getTopPadding } from '../../utils/sessionStorage'
import { useLocation, useParams } from 'react-router-dom'
import BackButton from '../../components/ui-components/BackButton'
import { useMemo } from 'react'
import { useStack } from '../StackProvider'

const demoPages = [1, 2, 3, 4]

const pageThemeById: Record<number, string> = {
  1: 'bg-gradient-to-br from-sky-100 via-blue-100 to-cyan-200',
  2: 'bg-gradient-to-br from-amber-100 via-orange-100 to-rose-200',
  3: 'bg-gradient-to-br from-emerald-100 via-green-100 to-lime-200',
  4: 'bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-800 text-white',
}

const cardClassById: Record<number, string> = {
  1: 'bg-white/80 border-blue-200',
  2: 'bg-white/80 border-orange-200',
  3: 'bg-white/80 border-emerald-200',
  4: 'bg-white/10 border-white/20',
}

const subtleTextClassById: Record<number, string> = {
  1: 'text-slate-700',
  2: 'text-slate-700',
  3: 'text-slate-700',
  4: 'text-slate-300',
}

const actionClassById: Record<number, string> = {
  1: 'bg-blue-100 text-blue-900',
  2: 'bg-orange-100 text-orange-900',
  3: 'bg-emerald-100 text-emerald-900',
  4: 'bg-white/15 text-white',
}

function getDemoRoute(page: number): string {
  return `/demo/stack/${page}`
}

export function StackDemoPage() {
  const stack = useStack()
  const location = useLocation()
  const { pageId } = useParams()
  const currentPage = useMemo(() => {
    const parsedPage = Number(pageId ?? '1')
    if (demoPages.includes(parsedPage)) return parsedPage
    return 1
  }, [pageId])
  const previousRoute = stack.previous
    ? `${stack.previous.pathname}${stack.previous.search}`
    : null

  return (
    <div
      className={`h-screen w-full overflow-y-auto no-scrollbar ${pageThemeById[currentPage]}`}
      style={{ paddingTop: `calc(${getTopPadding()}px + 12px)` }}
    >
      <div className="mx-auto w-full max-w-xl px-6 pb-20 h-max">
        <div className={`w-full rounded-2xl border p-6 shadow-lg ${cardClassById[currentPage]}`}>
          <div className="text-2xl font-semibold">Stack Demo Page {currentPage}</div>
          <div className={`mt-2 rounded border p-3 text-xs ${subtleTextClassById[currentPage]}`}>
            <div className="font-semibold">Live stack (latest on top)</div>
            <div className="mt-2 max-h-32 overflow-y-auto no-scrollbar rounded border border-black/10 bg-black/5 p-2">
              {[...stack.entries].reverse().map((entry, index) => (
                <div
                  key={`${entry.pathname}${entry.search}-${index}`}
                  className="mb-1 rounded bg-white/60 px-2 py-1 font-mono last:mb-0"
                >
                  {entry.pathname}
                  {entry.search}
                </div>
              ))}
            </div>
            <div className="mt-1">Current route: {location.pathname}</div>
          </div>

          <div className={`mt-4 text-sm ${subtleTextClassById[currentPage]}`}>
            {stack.canGoBack ? `Can go back to: ${previousRoute}` : 'Root of stack'}
          </div>

          <div className="mt-6 flex items-center gap-2">
            <BackButton
              onClick={() => {
                if (stack.canGoBack) stack.pop()
              }}
              fill={stack.canGoBack ? '#111827' : '#94a3b8'}
            />
            <div className={`${stack.canGoBack ? subtleTextClassById[currentPage] : 'text-slate-400'} text-sm`}>
              Back
            </div>
          </div>

          <div className="mt-6 text-sm font-semibold">Push</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {demoPages.map((page) => (
              <div
                key={`push-${page}`}
                className={`cursor-pointer rounded px-4 py-2 text-sm font-semibold ${actionClassById[currentPage]}`}
                onClick={() => stack.push(getDemoRoute(page))}
              >
                {page}
              </div>
            ))}
          </div>

          <div className="mt-4 text-sm font-semibold">Replace</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {demoPages.map((page) => (
              <div
                key={`replace-${page}`}
                className={`cursor-pointer rounded px-4 py-2 text-sm font-semibold ${actionClassById[currentPage]}`}
                onClick={() => stack.replace(getDemoRoute(page))}
              >
                {page}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
